import React, { useMemo, useState } from 'react';
import type { LegalBlock, ParagraphBlock, LegalRun } from '../../EdgeDocsCore/models/ast';
import { paginate, type PageSetup } from '../engine/LegalLayoutEngine';
import type { NumberingMap } from '../engine/NumberingResolver';
import { resolveRunStyle } from '../styles/resolveStyle';
import { getFontMetrics } from '../measurement/FontMetrics';
import { getStylesRegistry } from '../styles/registry';
import { pdfFromLayoutPlan } from '../pdf/pdfFromLayoutPlan';
import { exportPdfViaChromium } from '../pdf/chromiumClient';

const DEFAULT_SETUP: PageSetup = {
  pageWidthPx: 816, // 8.5in @ 96dpi minus margins later
  pageHeightPx: 1056, // 11in
  marginTopPx: 72,
  marginRightPx: 72,
  marginBottomPx: 72,
  marginLeftPx: 72,
  headerHeightPx: 24,
  footerHeightPx: 36,
  widowOrphanDefault: { minPrev: 2, minNext: 2 },
};

type Fixture = { name: string; build: () => LegalBlock[] };

const FIXTURES: Fixture[] = [
  {
    name: 'base_paragraph',
    build: () => [{ id: 'p1', type: 'Paragraph', attrs: {}, children: [{ id: 'r1', text: 'Hello world '.repeat(80) }]} as any],
  },
  {
    name: 'heading_keep_with_next',
    build: () => [
      { id: 'h1', type: 'Heading', attrs: { level: 2 }, children: [{ id: 'rh', text: 'Section' }], legalConstraints: { keepWithNext: true } } as any,
      { id: 'p2', type: 'Paragraph', attrs: {}, children: [{ id: 'r2', text: 'Following paragraph '.repeat(60) }]} as any,
    ],
  },
  {
    name: 'widows_orphans_split',
    build: () => [{ id: 'p3', type: 'Paragraph', attrs: {}, children: [{ id: 'r3', text: 'A long paragraph to test seam logic. '.repeat(120) }]} as any],
  },
  {
    name: 'atomic_at_bottom',
    build: () => [
      { id: 'p4', type: 'Paragraph', attrs: {}, children: [{ id: 'r4', text: 'Filling content. '.repeat(150) }]} as any,
      { id: 'img1', type: 'Image', attrs: { src: '#', width: 400, height: 400 }, legalConstraints: { noSplit: true } } as any,
    ],
  },
  {
    name: 'footnotes_overflow',
    build: () => [{ id: 'p5', type: 'Paragraph', attrs: {}, children: [
      { id: 'r5a', text: 'Body with anchors '.repeat(60) },
      { id: 'r5b', text: '', annotations: { footnoteAnchorId: 'fn1' } },
      { id: 'r5c', text: ' more content '.repeat(60) },
      { id: 'r5d', text: '', annotations: { footnoteAnchorId: 'fn2' } },
    ]} as any],
  },
  {
    name: 'cross_refs',
    build: () => [
      { id: 'h7', type: 'Heading', attrs: { level: 2 }, children: [{ id: 'rh7', text: 'Target Section' }]} as any,
      { id: 'p7', type: 'Paragraph', attrs: {}, children: [
        { id: 'r7a', text: 'See ' },
        { id: 'r7b', text: '§ 0 on page 0', annotations: { crossRef: { targetBlockId: 'h7', format: '§ {number} on page {page}' } } },
        { id: 'r7c', text: ' for details.' },
      ]} as any,
    ],
  },
  {
    name: 'cross_refs_demo',
    build: () => [
      { id: 'h10', type: 'Heading', attrs: { level: 2 }, children: [{ id: 'rh10', text: 'Target A' }]} as any,
      { id: 'h11', type: 'Heading', attrs: { level: 2 }, children: [{ id: 'rh11', text: 'Target B' }]} as any,
      { id: 'p10', type: 'Paragraph', attrs: {}, children: [
        { id: 'r10a', text: 'Multiple refs: ' },
        { id: 'r10b', text: '[A]', annotations: { crossRef: { targetBlockId: 'h10', format: 'A' } } },
        { id: 'r10c', text: ' and ' },
        { id: 'r10d', text: '[B]', annotations: { crossRef: { targetBlockId: 'h11', format: 'B' } } },
        { id: 'r10e', text: ' on this page.' },
      ] } as any,
      { id: 'p11', type: 'Paragraph', attrs: {}, children: [
        { id: 'r11a', text: 'Filler ' + 'text '.repeat(200) },
      ] } as any,
    ],
  },
  {
    name: 'cross_refs_numbered',
    build: () => [
      { id: 'sec1', type: 'Section', attrs: {}, legalConstraints: { noSplit: true } } as any,
      { id: 'c1', type: 'Clause', attrs: { clauseId: 'clause-1', clauseTitle: 'Definitions' }, children: [ { id: 'rc1', text: 'Clause body text.' } ] } as any,
      { id: 'p8', type: 'Paragraph', attrs: {}, children: [
        { id: 'r8a', text: 'Refer to ' },
        { id: 'r8b', text: '§ x on page y', annotations: { crossRef: { targetBlockId: 'c1', format: '§ {number} on page {page}' } } },
        { id: 'r8c', text: ' for the definition.' },
      ] } as any,
    ],
  },
  {
    name: 'nested_lists',
    build: () => [
      { id: 'pnl', type: 'Paragraph', attrs: {}, children: [{ id: 'rnla', text: 'Nested list demo:' }] } as any,
      { id: 'list1', type: 'List', attrs: { kind: 'ordered' }, children: [
        { id: 'li1', type: 'ListItem', attrs: {}, children: [ { id: 'rli1', text: 'Item 1' }, { id: 'sub1', type: 'List', attrs: { kind: 'ordered' }, children: [
          { id: 'li1a', type: 'ListItem', attrs: {}, children: [ { id: 'rli1a', text: 'Sub 1.a' } ] } as any,
          { id: 'li1b', type: 'ListItem', attrs: {}, children: [ { id: 'rli1b', text: 'Sub 1.b' } ] } as any,
        ] } ] } as any,
        { id: 'li2', type: 'ListItem', attrs: {}, children: [ { id: 'rli2', text: 'Item 2' } ] } as any,
      ] } as any,
      { id: 'p_refs', type: 'Paragraph', attrs: {}, children: [
        { id: 'rref0', text: 'Refs: ' },
        { id: 'rref1', text: '', annotations: { crossRef: { targetBlockId: 'li1', format: '{number}' } } },
        { id: 'rref2', text: ' / ' },
        { id: 'rref3', text: '', annotations: { crossRef: { targetBlockId: 'li1a', format: '{number}' } } },
        { id: 'rref4', text: ' / ' },
        { id: 'rref5', text: '', annotations: { crossRef: { targetBlockId: 'li1b', format: '{number}' } } },
        { id: 'rref6', text: ' / ' },
        { id: 'rref7', text: '', annotations: { crossRef: { targetBlockId: 'li2', format: '{number}' } } },
      ] } as any,
    ] as any,
  },
  {
    name: 'long_document',
    build: () => {
      const blocks: any[] = [];
      for (let i = 1; i <= 60; i++) {
        if (i % 8 === 1) {
          blocks.push({ id: `h${i}`, type: 'Heading', attrs: { level: 2 }, children: [{ id: `rh${i}`, text: `Section ${i}` }] });
        }
        blocks.push({ id: `p${i}`, type: 'Paragraph', attrs: {}, children: [{ id: `r${i}`, text: (`Paragraph ${i} — ` + 'lorem ipsum dolor sit amet, consectetur adipiscing elit. ').repeat(12) }] });
      }
      return blocks as any;
    },
  },
];

export default function PageLayoutViewer(): React.JSX.Element {
  const [fixtureName, setFixtureName] = useState<string>(FIXTURES[0].name);
  const [hashes, setHashes] = useState<{ plan?: string; pages?: string[]; prevPlan?: string; prevPages?: string[] }>({});
  const [plan, setPlan] = useState<any>(null);
  const [policy, setPolicy] = useState<{ prev: number; next: number }>({ prev: 2, next: 2 });
  const [showBoxes, setShowBoxes] = useState<boolean>(true);
  const [showText, setShowText] = useState<boolean>(false);
  const [debug, setDebug] = useState<boolean>(false);
  const [pdfDebugLinks, setPdfDebugLinks] = useState<boolean>(true);
  const [demoFontsReady, setDemoFontsReady] = useState<boolean>(false);
  const [demoFontConfigs, setDemoFontConfigs] = useState<any[] | null>(null);
  const [defaultExporter, setDefaultExporter] = useState<'quick'|'final'>('quick');
  const [chromiumPdfA, setChromiumPdfA] = useState<boolean>(true);
  const [lastExport, setLastExport] = useState<{ exporter: 'quick'|'final'; pdfa?: boolean; fontsLoaded?: number; families?: string[]; debugLinks?: boolean } | null>(null);
  const [chromiumDocMetaText, setChromiumDocMetaText] = useState<string>(JSON.stringify({ title: 'EdgeDocs Sample', author: 'EdgeLex', creator: 'EdgeDocs', versionId: 'v1', versionHash: 'abc123', planHash: 'N/A' }, null, 2));
  const [chromiumAiMetaText, setChromiumAiMetaText] = useState<string>(JSON.stringify({ aiAnnotations: [{ type: 'xref_proposal', payload: { source: 'p10', target: 'h10' } }] }, null, 2));
  const [chromiumFontsText, setChromiumFontsText] = useState<string>(JSON.stringify([
    { family: 'Inter', url: '/fonts/Inter-Regular.ttf', weight: 400, style: 'normal' },
    { family: 'Inter', url: '/fonts/Inter-Bold.ttf', weight: 700, style: 'normal' }
  ], null, 2));
  const [exportMs, setExportMs] = useState<number | null>(null);
  const [health, setHealth] = useState<{ ok?: boolean; puppeteer?: any; icc?: any } | null>(null);
  const [selected, setSelected] = useState<{ page: number; idx: number; box: any } | null>(null);
  const [error, setError] = useState<{ code: string; message: string; details?: any } | null>(null);
  const [checks, setChecks] = useState<{ pass: number; fail: number; issues: string[] }>({ pass: 0, fail: 0, issues: [] });
  const [numbering, setNumbering] = useState<NumberingMap | null>(null);

  const demoAst = useMemo<LegalBlock[]>(() => {
    const fx = FIXTURES.find(f => f.name === fixtureName) || FIXTURES[0];
    return fx.build();
  }, [fixtureName]);

  async function run() {
    try {
      setError(null);
      const res = await paginate(demoAst as any, { pageSetup: { ...DEFAULT_SETUP, widowOrphanDefault: { minPrev: policy.prev, minNext: policy.next } } });
      setPlan(res);
      // Extract numbering approximation from plan.crossRefs targets where possible (dev-only)
      // In a fuller devtools, paginate could expose numbering; for now infer from AST in this viewer if needed
      const inferred: NumberingMap = {};
      try {
        // Simple walk to mirror NumberingResolver for debug visuals
        let clauseCounter = 0; const headingCounters: Record<number, number> = {};
        for (const b of demoAst as any) {
          const t = (b as any).type as string;
          if (t === 'Section' || t === 'SectionBreak') { clauseCounter = 0; for (const k of Object.keys(headingCounters)) delete (headingCounters as any)[k]; continue; }
          if (t === 'Clause') { clauseCounter += 1; inferred[(b as any).id] = { displayNumber: String(clauseCounter), nestingLevel: 0 }; continue; }
          if (t === 'Heading') { const lvl = (b as any).attrs?.level ?? 1; headingCounters[lvl] = (headingCounters[lvl] || 0) + 1; inferred[(b as any).id] = { displayNumber: String(headingCounters[lvl]), nestingLevel: lvl }; }
          if (t === 'List') { const items = (b as any).children || []; for (let i = 0; i < items.length; i++) inferred[(items[i] as any).id] = { displayNumber: String(i + 1), nestingLevel: 1 }; }
        }
      } catch {}
      setNumbering(inferred);
      const pagesHash = (res.pages || []).map((p: any) => p.pageHash || '');
      setHashes((prev) => ({ prevPlan: prev.plan, prevPages: prev.pages, plan: res.planHash || '', pages: pagesHash }));
      // Run client-side assertions
      const result = evaluateInvariants(res, { minPrev: policy.prev, minNext: policy.next }, demoAst as any);
      setChecks(result);
    } catch (e: any) {
      setError({ code: e?.code || 'UNKNOWN', message: e?.message || String(e), details: e?.details });
    }
  }

  return (
    <div style={{ padding: 16 }}>
      <h2>EdgeDocs Page Layout Viewer</h2>
      <div style={{ marginBottom: 8 }}>
        <button onClick={run} style={{ padding: '6px 12px', background: '#2563eb', color: '#fff', borderRadius: 4, marginRight: 8 }}>Run Pagination</button>
        <button onClick={async () => {
          if (!plan) return;
          try {
            if (defaultExporter === 'quick') {
              const t0 = performance.now();
              // Quick (client) path: avoid custom font embedding (pdf-lib requires fontkit). Use fallback fonts.
              const bytes = await pdfFromLayoutPlan(plan, demoAst as any, { pageWidthPx: DEFAULT_SETUP.pageWidthPx, pageHeightPx: DEFAULT_SETUP.pageHeightPx, includeLinks: true, includeAI: true, debugLinks: pdfDebugLinks, pdfa: false });
              const blob = new Blob([bytes], { type: 'application/pdf' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = 'edgedocs.pdf'; a.click();
              URL.revokeObjectURL(url);
              setLastExport({ exporter: 'quick', fontsLoaded: demoFontConfigs?.length || 0, families: Array.from(new Set((demoFontConfigs||[]).map((f:any)=>f.family))), debugLinks: pdfDebugLinks });
              setExportMs(Math.round(performance.now() - t0));
            } else {
              const t0 = performance.now();
              const bytes = await exportPdfViaChromium(plan, demoAst as any, { pageWidthPx: DEFAULT_SETUP.pageWidthPx, pageHeightPx: DEFAULT_SETUP.pageHeightPx, pdfa: chromiumPdfA });
              const blob = new Blob([bytes], { type: 'application/pdf' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = chromiumPdfA ? 'edgedocs_final_pdfa.pdf' : 'edgedocs_final.pdf'; a.click();
              URL.revokeObjectURL(url);
              setLastExport({ exporter: 'final', pdfa: chromiumPdfA, fontsLoaded: demoFontConfigs?.length || 0, families: Array.from(new Set((demoFontConfigs||[]).map((f:any)=>f.family))) });
              setExportMs(Math.round(performance.now() - t0));
            }
          } catch (e) {
            alert(`Export failed: ${String(e)}`);
          }
        }} style={{ padding: '6px 12px', background: '#111827', color: '#fff', borderRadius: 4, marginRight: 8 }}>Export (Default)</button>
        <label style={{ fontSize: 12, marginRight: 8 }}>mode
          <select value={defaultExporter} onChange={(e)=>setDefaultExporter(e.target.value as any)} style={{ marginLeft: 6 }}>
            <option value="quick">Quick (client)</option>
            <option value="final">Final (server)</option>
          </select>
        </label>
        {defaultExporter === 'final' && (
          <label style={{ fontSize: 12, marginRight: 8 }}>
            <input type="checkbox" checked={chromiumPdfA} onChange={(e)=>setChromiumPdfA(e.target.checked)} /> PDF/A-2b
          </label>
        )}
        {defaultExporter === 'final' && (
          <details style={{ marginLeft: 8 }}>
            <summary style={{ cursor: 'pointer', fontSize: 12 }}>Chromium metadata & fonts</summary>
            <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 12, marginBottom: 4 }}>docMeta (JSON)</label>
                <textarea value={chromiumDocMetaText} onChange={(e)=>setChromiumDocMetaText(e.target.value)} rows={8} cols={32} style={{ fontFamily: 'monospace', fontSize: 12 }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 12, marginBottom: 4 }}>aiMeta (JSON)</label>
                <textarea value={chromiumAiMetaText} onChange={(e)=>setChromiumAiMetaText(e.target.value)} rows={8} cols={32} style={{ fontFamily: 'monospace', fontSize: 12 }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 12, marginBottom: 4 }}>fonts[] (JSON)</label>
                <textarea value={chromiumFontsText} onChange={(e)=>setChromiumFontsText(e.target.value)} rows={8} cols={32} style={{ fontFamily: 'monospace', fontSize: 12 }} />
              </div>
            </div>
          </details>
        )}
        {/* Existing exporters remain for explicit choice */}
        <button onClick={async () => {
          if (!plan) return;
          // Quick (client) path: avoid custom font embedding (pdf-lib requires fontkit). Use fallback fonts.
          const bytes = await pdfFromLayoutPlan(plan, demoAst as any, { pageWidthPx: DEFAULT_SETUP.pageWidthPx, pageHeightPx: DEFAULT_SETUP.pageHeightPx, includeLinks: true, includeAI: true, debugLinks: pdfDebugLinks, pdfa: false });
          const blob = new Blob([bytes], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = 'edgedocs.pdf'; a.click();
          URL.revokeObjectURL(url);
          setLastExport({ exporter: 'quick', fontsLoaded: demoFontConfigs?.length || 0, families: Array.from(new Set((demoFontConfigs||[]).map((f:any)=>f.family))), debugLinks: pdfDebugLinks });
        }} style={{ padding: '6px 12px', background: '#10b981', color: '#fff', borderRadius: 4, marginRight: 8 }}>Export PDF</button>
        <button onClick={async () => {
          if (!plan) return;
          try {
            let docMeta: any = undefined; let aiMeta: any = undefined; let fonts: any[] | undefined = undefined;
            try { docMeta = JSON.parse(chromiumDocMetaText || '{}'); } catch {}
            try { aiMeta = JSON.parse(chromiumAiMetaText || '{}'); } catch {}
            try { const parsed = JSON.parse(chromiumFontsText || '[]'); if (Array.isArray(parsed)) fonts = parsed; } catch {}
            const t0 = performance.now();
            const bytes = await exportPdfViaChromium(plan, demoAst as any, { pageWidthPx: DEFAULT_SETUP.pageWidthPx, pageHeightPx: DEFAULT_SETUP.pageHeightPx, pdfa: chromiumPdfA, docMeta, aiMeta, fonts });
            const blob = new Blob([bytes], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = chromiumPdfA ? 'edgedocs_chromium_pdfa.pdf' : 'edgedocs_chromium.pdf'; a.click();
            URL.revokeObjectURL(url);
            setLastExport({ exporter: 'final', pdfa: chromiumPdfA, fontsLoaded: demoFontConfigs?.length || 0, families: Array.from(new Set((demoFontConfigs||[]).map((f:any)=>f.family))) });
            setExportMs(Math.round(performance.now() - t0));
          } catch (e) {
            alert(`Chromium export failed: ${String(e)}`);
          }
        }} style={{ padding: '6px 12px', background: '#0ea5e9', color: '#fff', borderRadius: 4, marginRight: 8 }}>Export PDF (Chromium)</button>
        <button onClick={async () => {
          try {
            const { getEdgedocsApiBase } = await import('../pdf/chromiumClient');
            const r = await fetch(`${getEdgedocsApiBase()}/api/dev/edgedocs/export/health`);
            const j = await r.json();
            setHealth(j);
          } catch (e) {
            alert(`Health check failed: ${String(e)}`);
          }
        }} style={{ padding: '6px 12px', background: '#6b7280', color: '#fff', borderRadius: 4, marginRight: 8 }}>Check Exporter Health</button>
        <label style={{ fontSize: 12, marginRight: 8 }}>fixture
          <select value={fixtureName} onChange={(e) => setFixtureName(e.target.value)} style={{ marginLeft: 6 }}>
            {FIXTURES.map(f => <option key={f.name} value={f.name}>{f.name}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 12, marginRight: 8 }}>minPrev
          <input type="number" min={0} max={5} value={policy.prev} onChange={(e) => setPolicy((p) => ({ ...p, prev: Number(e.target.value) }))} style={{ width: 48, marginLeft: 6 }} />
        </label>
        <label style={{ fontSize: 12 }}>minNext
          <input type="number" min={0} max={5} value={policy.next} onChange={(e) => setPolicy((p) => ({ ...p, next: Number(e.target.value) }))} style={{ width: 48, marginLeft: 6 }} />
        </label>
        <label style={{ fontSize: 12, marginLeft: 12 }}>
          <input type="checkbox" checked={showBoxes} onChange={(e) => setShowBoxes(e.target.checked)} /> show boxes
        </label>
        <label style={{ fontSize: 12, marginLeft: 8 }}>
          <input type="checkbox" checked={showText} onChange={(e) => setShowText(e.target.checked)} /> show text
        </label>
        <label style={{ fontSize: 12, marginLeft: 8 }}>
          <input type="checkbox" checked={debug} onChange={(e) => setDebug(e.target.checked)} /> debug
        </label>
        <label style={{ fontSize: 12, marginLeft: 8 }}>
          <input type="checkbox" checked={pdfDebugLinks} onChange={(e) => setPdfDebugLinks(e.target.checked)} /> PDF debug links
        </label>
        <button onClick={async () => {
          try {
            const candidates = [
              { family: 'Inter', weight: 400, style: 'normal' as const, url: '/fonts/Inter-Regular.ttf' },
              { family: 'Inter', weight: 700, style: 'normal' as const, url: '/fonts/Inter-Bold.ttf' },
              { family: 'Inter', weight: 400, style: 'italic' as const, url: '/fonts/Inter-Italic.ttf' },
              { family: 'SourceSerif', weight: 400, style: 'normal' as const, url: '/fonts/SourceSerif4-Regular.ttf' },
              { family: 'SourceSerif', weight: 700, style: 'normal' as const, url: '/fonts/SourceSerif4-Bold.ttf' },
            ];
            const loaded: any[] = [];
            await Promise.all(candidates.map(async (c) => {
              try {
                const res = await fetch(c.url);
                if (!res.ok) return;
                const data = await res.arrayBuffer();
                loaded.push({ family: c.family, weight: c.weight, style: c.style, data });
              } catch {}
            }));
            setDemoFontConfigs(loaded);
            setDemoFontsReady(loaded.length > 0);
          } catch {
            setDemoFontsReady(false);
          }
        }} style={{ padding: '6px 12px', background: demoFontsReady ? '#065F46' : '#6B7280', color: '#fff', borderRadius: 4, marginLeft: 8 }} title="Attempts to load demo fonts from /fonts/* (optional).">{demoFontsReady ? 'Fonts Ready' : 'Load Demo Fonts'}</button>
      </div>
      {error && (
        <div style={{ background: '#FEF2F2', color: '#991B1B', padding: 8, marginBottom: 8, border: '1px solid #fecaca' }}>
          <strong>Error:</strong> {error.code} — {error.message}
          {error.details && (
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, marginTop: 4 }}>{JSON.stringify(error.details, null, 2)}</pre>
          )}
        </div>
      )}
      {/* PASS/FAIL summary */}
      {plan && (
        <div style={{ padding: 8, marginBottom: 8, border: '1px solid #e5e7eb', background: checks.fail ? '#FEF2F2' : '#ECFDF5', color: checks.fail ? '#991B1B' : '#065F46' }}>
          <strong>{checks.fail ? 'FAIL' : 'PASS'}</strong> — planHash stable: {hashes.prevPlan ? (hashes.prevPlan === hashes.plan ? 'YES' : 'NO') : 'n/a'} | checks: {checks.pass} passed, {checks.fail} failed
          {checks.issues.length > 0 && (
            <details style={{ marginTop: 6 }}>
              <summary style={{ cursor: 'pointer' }}>Details</summary>
              <ul style={{ marginTop: 6 }}>
                {checks.issues.map((m, i) => (<li key={i} style={{ fontSize: 12 }}>{m}</li>))}
              </ul>
            </details>
          )}
          {/* Export status panel */}
          <div style={{ marginTop: 8, fontSize: 12, color: '#374151', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <span><strong>default:</strong> {defaultExporter === 'quick' ? 'Quick (client, pdf-lib)' : `Final (server, Chromium${chromiumPdfA ? ', PDF/A' : ''})`}</span>
            {lastExport && (
              <span><strong>last export:</strong> {lastExport.exporter} {lastExport.pdfa ? '(PDF/A)' : ''} | fonts: {lastExport.fontsLoaded || 0} {lastExport.families && lastExport.families.length ? `(${lastExport.families.join(', ')})` : ''} {lastExport.debugLinks ? '| debug links' : ''}</span>
            )}
            {exportMs != null && (<span><strong>time:</strong> {exportMs} ms</span>)}
            {health && (<span title={JSON.stringify(health)}><strong>health:</strong> {health.ok ? 'OK' : 'NOT OK'}{health.icc?.exists ? ' | ICC' : ''}</span>)}
          </div>
        </div>
      )}
      <div style={{ marginBottom: 12, fontSize: 12, color: '#374151', display: 'flex', gap: 8, alignItems: 'center' }}>
        <span>planHash: {hashes.plan || '(n/a)'} {hashes.prevPlan && hashes.prevPlan !== hashes.plan ? <em style={{color:'#dc2626'}}>changed</em> : null}</span>
        <button onClick={() => navigator.clipboard.writeText(hashes.plan || '')} style={{ padding: '2px 6px', fontSize: 12 }}>Copy</button>
      </div>
      {plan && plan.pages && plan.pages.map((p: any, idx: number) => (
        <div key={idx} style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 12, color: '#6b7280', display: 'flex', gap: 8, alignItems: 'center' }}>
            <span>page {idx + 1} hash: {hashes.pages?.[idx] || '(n/a)'} {(hashes.prevPages && hashes.prevPages[idx] && hashes.prevPages[idx] !== hashes.pages?.[idx]) ? <em style={{color:'#dc2626'}}>changed</em> : null}</span>
            <button onClick={() => navigator.clipboard.writeText(hashes.pages?.[idx] || '')} style={{ padding: '2px 6px', fontSize: 12 }}>Copy</button>
          </div>
          <div style={{ position: 'relative', width: DEFAULT_SETUP.pageWidthPx, height: DEFAULT_SETUP.pageHeightPx, border: '1px solid #e5e7eb', background: '#fff' }}>
            {/* Body lines */}
            {p.body?.lineBoxes?.map((b: any, i: number) => {
              const onClick = () => setSelected({ page: idx, idx: i, box: b });
              const styleBoxes = showBoxes ? { border: '1px dashed rgba(37,99,235,0.4)' } : {};
              return (
                <div key={i} onClick={onClick} title={`line ${i}`} style={{ position: 'absolute', left: b.x, top: b.y, width: b.width, height: b.height, ...styleBoxes }} />
              );
            })}
            {/* Render text for lines */}
            {showText && p.body?.lineBoxes?.map((b: any, i: number) => {
              if (!b || b.width <= 0 || b.endGrapheme < b.startGrapheme) return null;
              const block = (demoAst as any).find((n: any) => n.id === b.blockId) as ParagraphBlock | undefined;
              if (!block) return null;
              const runs = (block.children || []) as LegalRun[];
              const run = runs.find(r => r.id === b.runId) as LegalRun | undefined;
              if (!run) return null;
              const paragraphStyle = block.styleId ? getStylesRegistry().paragraphStyles[block.styleId] : undefined;
              const rs = resolveRunStyle(paragraphStyle as any, run);
              const full = run.text || '';
              // Cross-reference display: rewrite placeholder when annotation exists
              const cf = run.annotations?.crossRef;
              if (cf) {
                // Prefer engine-produced crossRefs
                const cr = (plan?.crossRefs || []).find((x: any) => x.sourceRunId === run.id);
                const formatted = cr?.status === 'resolved'
                  ? (cr.display || '')
                  : `[unresolved:${cf.targetBlockId}]`;
                const ascent = Math.round(getFontMetrics(rs).ascentPx);
                return (
                  <span key={`t-${i}`}
                    title={cr ? `xref → target=${cr.targetBlockId}${cr.targetPage ? ` page=${cr.targetPage}` : ''} status=${cr.status}` : `xref → target=${cf.targetBlockId}`}
                    style={{ position: 'absolute', left: b.x, top: b.baseline - ascent, fontFamily: rs.fontFamily, fontSize: `${Math.round(rs.fontSizePt * (96/72))}px`, whiteSpace: 'pre', color: cr?.status === 'unresolved' ? '#b91c1c' : undefined }}>
                    {formatted}
                  </span>
                );
              }
              // Grapheme-accurate slicing for overlay
              let segment = '';
              try {
                const seg = new (Intl as any).Segmenter(undefined, { granularity: 'grapheme' });
                const parts = Array.from(seg.segment(full)).map((s: any) => s.segment);
                segment = parts.slice(b.startGrapheme, b.endGrapheme + 1).join('');
              } catch {
                segment = full.slice(b.startGrapheme, b.endGrapheme + 1);
              }
              const ascent = Math.round(getFontMetrics(rs).ascentPx);
              return (
                <span key={`t-${i}`} style={{ position: 'absolute', left: b.x, top: b.baseline - ascent, fontFamily: rs.fontFamily, fontSize: `${Math.round(rs.fontSizePt * (96/72))}px`, whiteSpace: 'pre' }}>{segment}</span>
              );
            })}
            {/* Header/Footer regions */}
            <div style={{ position: 'absolute', left: p.header.x, top: p.header.y, width: p.header.width, height: p.header.height, background: 'rgba(16,185,129,0.08)' }} />
            <div style={{ position: 'absolute', left: p.footer.x, top: p.footer.y, width: p.footer.width, height: p.footer.height, background: 'rgba(245,158,11,0.08)' }} />
            {/* Debug numbering overlay: show numbering entry near top-left of each block's first line */}
            {debug && numbering && p.body?.lineBoxes?.map((bx: any, j: number) => {
              const num = numbering[bx.blockId];
              if (!num) return null;
              return (
                <span key={`n-${j}`} style={{ position: 'absolute', left: bx.x + 2, top: bx.y + 2, fontSize: 10, color: '#2563eb', background: 'rgba(37,99,235,0.06)', padding: '1px 3px', borderRadius: 3 }}>{num.displayNumber}</span>
              );
            })}
          </div>
        </div>
      ))}
      {/* Inspector */}
      {selected && (
        <div style={{ position: 'fixed', right: 16, top: 80, width: 320, background: '#fff', border: '1px solid #e5e7eb', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: 12 }}>
          <div style={{ marginBottom: 8, fontWeight: 600 }}>Inspector</div>
          <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{JSON.stringify(selected.box, null, 2)}</pre>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => navigator.clipboard.writeText(JSON.stringify(selected.box))} style={{ padding: '4px 8px', fontSize: 12 }}>Copy LineBox</button>
            <button onClick={() => setSelected(null)} style={{ padding: '4px 8px', fontSize: 12 }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Basic client-side invariants for PASS/FAIL display
function evaluateInvariants(plan: any, policy: { minPrev: number; minNext: number }, ast: LegalBlock[]) {
  let pass = 0; let fail = 0; const issues: string[] = [];
  const pages = plan?.pages || [];
  // 1) Atomic blocks should not be split across pages (use AST types/constraints)
  const atomicIds = new Set<string>();
  for (const b of (ast || [])) {
    const t = (b as any).type as string;
    const isAtomic = (b as any).legalConstraints?.noSplit === true || t === 'Image' || t === 'Table' || t === 'PageBreak' || t === 'SectionBreak';
    if (isAtomic) atomicIds.add((b as any).id);
  }
  // Build map blockId -> set of pages it appears on
  const blockToPages: Record<string, Set<number>> = {};
  pages.forEach((pg: any, idx: number) => (pg.body?.lineBoxes || []).forEach((lb: any) => {
    (blockToPages[lb.blockId] ||= new Set<number>()).add(idx);
  }));
  Object.entries(blockToPages).forEach(([bid, set]) => {
    if (set.size > 1 && atomicIds.has(bid)) {
      fail++; issues.push(`Atomic block appears on multiple pages: ${bid}`);
    }
  });
  // 2) Widow/Orphan seam: when seam splits same paragraph, check minPrev/minNext
  for (let i = 0; i < pages.length - 1; i++) {
    const a = pages[i].body.lineBoxes || [];
    const b = pages[i + 1].body.lineBoxes || [];
    if (!a.length || !b.length) continue;
    const last = a[a.length - 1]; const first = b[0];
    if (last.blockId === first.blockId) {
      const prevCount = a.filter((l: any) => l.blockId === last.blockId).length;
      const nextCount = b.filter((l: any) => l.blockId === last.blockId).length;
      if (prevCount < policy.minPrev || nextCount < policy.minNext) {
        fail++; issues.push(`Seam widow/orphan: paragraph ${last.blockId} prev=${prevCount} next=${nextCount}`);
      }
    }
  }
  // 3) Cross-ref resolution: any run with annotations.crossRef should resolve to a page >= 1
  // Since viewer performs inline substitution, we check presence of the target block in any page
  const anyPages = pages.length;
  if (anyPages > 0) pass++; else { fail++; issues.push('No pages generated'); }
  return { pass, fail, issues };
}


